// ═══════════════════════════════════════════════════════════════════════════
// grainAction.js — the deterministic Grain action layer (ROADMAP.md §1.2
// Stages 2, 4, 6): economics, market context, and a status/amount/reason/
// trigger, built ON TOP OF the Stage-1 GrainPosition. No AI narrative — every
// field here is arithmetic over real inputs, per §1.2's "AI never chooses the
// recommendation."
//
// REUSES, DOES NOT REBUILD, the existing market engine:
//   grainContractTech / grainFillCurve / grainTargetLadder / grainBoardHorizon
//   / grainBreakeven — all from src/grainTargets.js, already a shared,
//   CommonJS, App.jsx-AND-Vercel-importable module (nothing to extract; it was
//   already done, 2026-07-14, "one ladder shared by both tools").
//
// AND REUSES copRules.js for the cost side: copSum(mode, crop, line_items,
// insurancePerAcre) is the SAME net $/acre the Cost-of-Production tool shows
// a farmer — never a second cost calculation growing up beside it.
//
// ── BREAKEVEN IS CONTEXT, NOT A GATE (Kevin, 2026-07-14, src/grainTargets.js
// header) ── "The market does not care about your cost of production... just
// because grain is below cost of production does not always mean you
// shouldn't be selling." So `grainTargetLadder`'s rungs are computed WITHOUT
// consulting breakeven at all (unchanged — this file passes breakeven through
// for DISPLAY and for the required-remaining-price metric, never into the
// ladder). The status ladder below is driven by MARKET signals (percentile,
// regime, time remaining on the board) with economics attached as a reason,
// never as a precondition — the one exception is a below-breakeven sale
// showing up explicitly as a reason code, not a block.
//
// PURE — no fetch, no React, no Supabase.
// ═══════════════════════════════════════════════════════════════════════════

import { copSum } from "../copRules";
import { grainTargetLadder, grainBreakeven } from "../grainTargets";
import { resolveBoardHorizon, BOARD_STATE } from "./grainHorizon";
// The observation date is an ARGUMENT to resolveBoardHorizon on purpose — a
// horizon that reads the clock inside the calculation is not reproducible.
// This is the one place the clock is read, at the boundary.
const todayISO = () => new Date().toISOString().slice(0, 10);
import { roundDownToLot } from "./grainCash";

// ── SALE_STEP_PCT — the most one recommendation may ask for, as a share of
// EXPECTED PRODUCTION. Kevin, 2026-08-21: telling a farm 52,000 bu behind
// its target to sell 52,000 bu today is not a recommendation anyone acts on.
// A target range is a destination reached in steps; this is one step.
//
// Off expected production rather than the remaining gap ON PURPOSE:
// grainTargetPosition.js records why `uncommitted * 0.15` was retired — a
// fixed share of an ever-shrinking remainder never converges. A fixed
// denominator does, and it keeps every step the same size.
export const SALE_STEP_PCT = 0.10;
import { opportunityCategory } from "./grainCurve";
import { yearRankPercentile } from "./futuresContractHistory";
import { deriveStorageExposure } from "./grainStorage";
import { resolveHarvestExecution } from "./grainHarvestExecution";
import { classifySaleReason } from "./grainSaleReason";
import { resolveGrainTargetGapBu } from "./grainTargetPosition";

// ROADMAP.md §1.2 standard statuses — identical across Grain/DRP/LRP.
export const STATUS = { NO_ACTION: "NO ACTION", WATCH: "WATCH", ATTRACTIVE: "ATTRACTIVE", STRONG: "STRONG", URGENT: "URGENT" };

// ── Cost side: reuses copRules.js's real cost engine ─────────────────────────
// grainCopLine = a grain_cop row's { crop_year, crop, line_items } (streamlined
// or detailed — copSum reads COP_CATEGORIES[mode] to know which). Returns null
// (never a fabricated number) when there is no COP row for this farm/crop —
// this is the SAME gate DrpPosition applies to expectedMilkLb: an unentered
// input withholds the number it feeds, it does not get filled with a
// benchmark by this file (Phase I-A's benchmark ladder is separate, not-yet-
// built work — see ROADMAP.md Phase I).
export function resolveGrainCop({ mode, crop, lineItems, yieldPerAcre, insurancePerAcre = null }) {
  if (!lineItems || !yieldPerAcre) return null;
  const byproductKey = mode === "streamlined" ? "s_straw" : "straw";
  const byproductPerAcre = Number(lineItems[byproductKey]) || 0;
  const costPerAcre = copSum(mode, crop, lineItems, insurancePerAcre);
  const breakEvenPerBu = grainBreakeven(costPerAcre, yieldPerAcre, byproductPerAcre);
  return { costPerAcre, yieldPerAcre, byproductPerAcre, breakEvenPerBu, mode };
}

// ── Required average price on remaining bushels ──────────────────────────────
// ROADMAP.md §1.2 "the best idea in the spec": (target total revenue − already
// contracted revenue) ÷ remaining bushels. Target total revenue uses the
// whole-crop BREAKEVEN as the target (this file computes no margin target —
// that is Phase I's target-margin price, a later refinement). "Remaining" is
// bushels not yet PRICED (expectedBu − pricedBu) — a contracted-but-unpriced
// bushel (HTA, basis-open) still needs a price set, so it belongs in the
// denominator, matching GrainPosition.currentProtection's own pricedBu/
// unpricedBu split.
//
// Per §1.2's own display constraint: this number FALLS as a farm sells well
// early, which can misleadingly make a weakening market look fine. The caller
// must show it BESIDE the standalone breakEvenPerBu, never alone — both are
// returned here for exactly that reason.
export function requiredRemainingPrice({ expectedBu, pricedBu, pricedRevenue, breakEvenPerBu }) {
  if (breakEvenPerBu == null || expectedBu == null || !(expectedBu > 0)) return null;
  const priced = pricedBu != null ? pricedBu : 0;
  const revenue = pricedRevenue != null ? pricedRevenue : 0;
  const remainingBu = Math.max(0, expectedBu - priced);
  if (remainingBu <= 0) return { requiredPrice: null, remainingBu: 0 };
  const targetRevenue = breakEvenPerBu * expectedBu;
  const requiredPrice = (targetRevenue - revenue) / remainingBu;
  return { requiredPrice, remainingBu };
}

// ── The deterministic status ladder ─────────────────────────────────────────
// Market-driven (percentile of the board's own life-to-date range + regime +
// time remaining on THIS board), never gated on breakeven — see header. Every
// status carries its reasons[] as plain, numeric statements (ROADMAP.md §1.2:
// "never a vibe").
//
// boardClosed / contractDataUnknown — found live 2026-08-17 auditing why
// Book View showed 98/151/197/2 (URGENT/STRONG/ATTRACTIVE/NO ACTION), and
// why wheat specifically was URGENT for nearly every farm at only the 63rd
// percentile. Root-caused against the real book (tools/check-grain-action.js
// carries the regression cases), not tuned by adjusting a threshold:
//   1. grainBoardHorizon() FLOORS at 15 trading days for a contractDate that
//      has already passed (wheat CY2026's July board, evaluated in August —
//      the board closed ~48 calendar days before this ran). That floor is
//      real for a board that is genuinely 15 days from expiry; it is a false
//      "always urgent" signal for a board that has ALREADY expired, because
//      the time-decay rule below cannot tell the two apart from horizonDays
//      alone. boardClosed distinguishes them using the RAW (unclamped)
//      days-to-contract, computed once in buildGrainAction and passed in.
//   2. uncommittedFrac used to coerce an UNKNOWN contractedBu (zero grain_
//      contracts rows on file for this crop/year — GrainPosition's own
//      convention, grainRules.js: `contractedBu: rows.length ? contractedBu
//      : null`) into a CONFIRMED-ZERO-committed 100%-open position. Kevin,
//      2026-08-17: "Silas has no contract record must not automatically mean
//      the farmer has sold zero grain... Unknown and zero are different." 94
//      of 98 wheat URGENT rows and 145 of 151 soybean STRONG rows had NO
//      grain_contracts record at all. contractDataUnknown is true exactly
//      when GrainPosition itself reports contractedBu as unknown (null, not
//      0), and uncommittedFrac is withheld (null) rather than assumed in
//      that case — the percentile/regime read is still real and still
//      shown, only the "how much is exposed" claim (and anything sized off
//      it) is withheld.
//   3. A THIRD, related conflation found the same day on real data — Kevin
//      looking at Meisenzahl's live Wheat 2026 card: "there is no way 87% is
//      unpriced." The card's denominator used to be `pricedBu` (cash price
//      LOCKED), which folds a real HTA/basis-open CONTRACT — futures leg
//      locked, only the cash price still open, 8,000 of Meisenzahl's 14,916
//      bu — into the same "unpriced" bucket as bushels with NO contract at
//      all. Those are different actions (a new sale vs. calling the
//      elevator to set a basis on an existing contract), so urgency and
//      sizing now key off `uncommittedFrac` (expectedBu − contractedBu, the
//      TRUE no-decision-made-yet exposure); `contractedUnpricedBu`
//      (contractedBu − pricedBu) is surfaced as its own, separate,
//      non-urgency-driving reason.
function statusFor({ pctile, regime, horizonDays, uncommittedFrac, contractedUnpricedBu, belowBreakeven, belowBreakevenPriceLabel, boardClosed, contractDataUnknown, hasExposure }) {
  const reasons = [];
  // MARKET MODE (2026-08-18 "progressively useful" checkpoint): a farm/crop/
  // year with no RMA coverage resolved at all still gets a real market read
  // below — this reason says so up front rather than silently degrading. This
  // is a DIFFERENT, stronger condition than contractDataUnknown (which can
  // still have a known expectedBu denominator); when exposure itself is
  // unknown there is no denominator, so nothing below can size a bushel
  // figure or claim a %-sold, but the percentile/regime read is still real
  // and still worth showing (Kevin, 2026-08-18: "Silas must be useful from
  // almost no farm data").
  if (!hasExposure) {
    reasons.push("no RMA-priceable exposure resolved for this farm/crop/year — this is market context only, not a farm-specific % sold or sizing");
  }
  // ⚠ THIS USED TO READ THE BOARD'S OWN LIFE-TO-DATE PERCENTILE, AND IT WAS
  // STRUCTURALLY 100th FOR EVERY CONTRACT. futures_contract_history stores an
  // EXPIRED contract as a ~250-bar one-year segment and a LIVE one as its whole
  // trading life, so a live contract is ranked against a window its predecessors
  // never had. Measured on the live board 2026-09-03, all twenty live corn,
  // soybean and wheat contracts read 98.6-100.0 — so `pctile >= 0.8 -> STRONG`
  // below returned STRONG for every farm, every crop, every day.
  //
  // A trailing one-year window does NOT fix it and was measured before being
  // discarded: it moves CZ26 from 99.7 to 99.2 and leaves the whole board at
  // 88-100, because these markets really are near their own 12-month highs. The
  // shape bias and the rally are not separable that way.
  //
  // `pctile` is now the CROSS-YEAR rank — this contract month against the SAME
  // month in prior years at the same point before delivery — which is the
  // comparison a marketer actually makes and which spreads 50.0-100.0 over the
  // same twenty contracts. When that cannot be computed the market leg is
  // WITHHELD rather than falling back to the biased figure.
  const p = pctile != null ? Math.round(pctile * 100) : null;
  if (p != null) {
    reasons.push(`price sits at the ${p}th percentile of what this contract month has done in prior years at the same point before delivery`);
  } else {
    reasons.push("no cross-year comparison available for this contract month at this point in its life — the market read is withheld rather than ranked against this board's own history, which is not comparable across contracts");
  }
  if (regime) reasons.push(`board is in a measured ${regime}`);
  if (hasExposure && contractDataUnknown) {
    reasons.push("no grain_contracts record on file for this crop/year — committed % is UNKNOWN, not confirmed zero; sizing and time-decay urgency are withheld until a contract position is entered");
  } else if (uncommittedFrac != null) {
    reasons.push(`${Math.round(uncommittedFrac * 100)}% of expected production has no contract at all`);
  }
  if (!contractDataUnknown && contractedUnpricedBu > 0) {
    reasons.push(`${Math.round(contractedUnpricedBu)} bu already under contract but cash price not yet set (HTA/basis-open) — a pricing call on an existing commitment, not a new sale`);
  }
  if (boardClosed) {
    reasons.push("this crop-year's marketing board has already passed its delivery month — time-decay urgency cannot be assessed against it; treat this as a percentile-only read");
  } else if (horizonDays != null) {
    reasons.push(`~${horizonDays} trading days left before this board stops being the pricing reference`);
  }
  const priceLabel = belowBreakevenPriceLabel || "today's board price";
  if (belowBreakeven === true) reasons.push(`${priceLabel} is below the farm's own breakeven — shown as context, not a reason to hold (grainTargets.js: the market doesn't price to your cost)`);
  if (belowBreakeven === false) reasons.push(`${priceLabel} is above the farm's own breakeven`);

  // Time decay: a short horizon with meaningful uncommitted bushels outranks
  // percentile — ROADMAP.md §1.2 "exposure eventually outranks percentile."
  // Never fires on a closed board or an unknown contract position — both are
  // "cannot assess," not "assess as maximally urgent." `hasExposure` is
  // structurally redundant here today (uncommittedFrac is already always
  // null when hasExposure is false, since it is derived from expectedBu,
  // which buildGrainAction only ever sets from position.exposure) — kept
  // explicit anyway (Kevin, 2026-08-18: "a strong futures percentile with no
  // position must never manufacture URGENT/sizing") so this invariant does
  // not silently depend on that transitive chain surviving a future refactor.
  const urgent = hasExposure && !boardClosed && !contractDataUnknown
    && horizonDays != null && horizonDays <= 30 && uncommittedFrac != null && uncommittedFrac >= 0.3;
  if (urgent) return { status: STATUS.URGENT, reasons: [...reasons, `only ~${horizonDays} trading days left with ${Math.round(uncommittedFrac * 100)}% still uncommitted`] };

  if (!contractDataUnknown && uncommittedFrac != null && uncommittedFrac <= 0.02) return { status: STATUS.NO_ACTION, reasons: [...reasons, "effectively fully committed — nothing left to act on"] };

  if (pctile != null) {
    if (pctile >= 0.8) return { status: STATUS.STRONG, reasons };
    if (pctile >= 0.6) return { status: STATUS.ATTRACTIVE, reasons };
    if (pctile >= 0.35) return { status: STATUS.WATCH, reasons };
    return { status: STATUS.NO_ACTION, reasons: [...reasons, "price is in the lower part of its own range — no market signal to act on today"] };
  }
  return { status: STATUS.WATCH, reasons: [...reasons, "insufficient market history to rank this board — defaulting to WATCH rather than a stronger claim"] };
}

// ── GrainAction ───────────────────────────────────────────────────────────
// position: a GrainPosition (from grainPosition.js).
// cop: resolveGrainCop() output, or null (economics withheld, not invented).
// contractBars: this board's OWN daily bars (futures_contract_history — the
//   SAME per-contract series grainTargetLadder was built for).
// continuousBars: the continuous front-month series (futures_history_daily),
//   for grainFillCurve — optional; grainTargetLadder degrades gracefully
//   (fallback fill odds) without it.
// contractDate: the board's delivery/first-notice-relevant date, for
//   grainBoardHorizon (trading days this order has left to work).
// cash: resolveGrainCash() output (src/positions/grainCash.js), or null/undefined
//   when the caller hasn't resolved it. Kevin, 2026-08-17: "the farm-level sale
//   recommendation and economic comparison must use a realizable CASH price
//   whenever basis is available." When cash.available is true it drives the
//   breakeven comparison and the before/after scenario's assumed price;
//   futures (tc.last) remains the fallback for BOTH — never silently promoted
//   to "cash," always labeled by which one was actually used.
// storage: resolveGrainStorage() output (src/positions/grainStorage.js), or
//   null/undefined when the caller hasn't resolved it. Kevin, 2026-08-18
//   20:40: a PURE data/model seam — passed straight through onto the return
//   object, unread by every calculation above (status, sizing, scenario,
//   yield-risk are all computed with zero reference to it). No calendar
//   deadline, forced-sale %, or carrying-cost economics reads this field yet;
//   that is a distinct, future "marketing clock" layer with its own
//   methodology, not a side effect of adding this input. Kevin, 2026-08-18
//   21:45: the ONE addition this checkpoint makes is `storage.exposure` — the
//   mechanical expectedCropBu-vs-storageCapacityBu split
//   (deriveStorageExposure(), src/positions/grainStorage.js) — still pure
//   arithmetic, still read by nothing above and reading nothing above; it
//   does not turn storage into a second sizing input.
// regime (2026-08-18 "GRAIN MARKET INTELLIGENCE UI"): grainRegime.js's
//   classifyMarketRegime() output (and, optionally, a caller-resolved
//   scoreRegimeRelativePercentile() score attached to it as `.crossYear`),
//   or null/undefined when the caller hasn't computed it. PURE PASSTHROUGH,
//   like `storage` above — nothing below reads it, and it reads nothing
//   computed below. `market.regime` (the existing trend-only up/downtrend
//   string, unchanged) and this field answer DIFFERENT questions: this one
//   is the multi-dimensional volatility/curve/trend classifier, kept
//   structurally separate per Kevin's "must remain separate and visible;
//   never collapse them into one opaque score."
export function buildGrainAction({ position, cop, tc, curve, contractDate, cash = null, storage = null, regime = null, asOf = null, futuresYearRank = null }) {
  // MARKET MODE (2026-08-18): a farm/crop/year with no RMA-priceable
  // exposure at all used to short-circuit to a bare stub with market:null,
  // cash:null — hiding real board/basis/cash context that resolveGrainCash()
  // and grainTargets.js can compute independently of any farm position.
  // grainPosition.js's own positionBannerState() header already named this
  // "a real gap, not fixed here" (2026-08-18). Fixed here: only the
  // genuinely position-dependent legs (contract completeness, bushel sizing,
  // the before/after scenario, yield-risk tiers) are gated on hasExposure —
  // market/cash/economics context resolves the same whether or not a
  // position exists, exactly like the Position/Futures/Basis gauges already
  // do (they fetch independently of `action` for this reason).
  const hasExposure = !!(position && position.exposure);
  const expectedBu = hasExposure ? position.exposure.expectedBu : null;
  // contractedBu is GrainPosition's own unknown-vs-zero signal (grainRules.js
  // summarizeGrainContracts: null when there are literally zero grain_contracts
  // rows for this crop/year, 0 only when rows exist and sum to nothing). Reuse
  // it rather than re-deriving unknown from pricedBu — one signal, one place.
  // Optional-chained on `position` itself (never assumed non-null) so a
  // no-exposure caller degrades instead of throwing.
  const contractDataUnknown = position?.currentProtection?.contractedBu == null;
  const contractedBu = position?.currentProtection?.contractedBu ?? null;
  const pricedBu = position?.currentProtection?.pricedBu ?? null;
  const avgPricedCashPrice = position?.currentProtection?.avgPricedCashPrice ?? null;
  // commitment — grainPosition.js's grainCommitment.js split (Kevin,
  // 2026-08-19: physical commitment vs price protection are NOT
  // interchangeable). priceProtectedBu/pctPriceProtected anchor the
  // target-range gap sizing below; basisProtectedBu/physicalOnlyBu are
  // surfaced on the return object for the caller, never folded into sizing.
  const commitment = position?.currentProtection?.commitment ?? null;
  const priceProtectedBu = commitment?.priceProtectedBu ?? null;
  const pctPriceProtected = commitment?.pctPriceProtected ?? null;
  // uncommittedFrac drives urgency/sizing — it is what still has NO marketing
  // decision at all. Found live 2026-08-17, Kevin looking at Meisenzahl's real
  // Wheat 2026 card: "there is no way 87% is unpriced" — the OLD unpricedFrac
  // was (expectedBu - pricedBu)/expectedBu, which is CASH price coverage, and
  // folded a real HTA/basis-open CONTRACT (futures leg locked, only the cash
  // price open — 8,000 of Meisenzahl's 14,916 bu) into the same bucket as
  // bushels with NO contract at all. Those are different actions: "make a new
  // sale" vs "call the elevator to set a basis/cash price on an existing
  // contract." grainRules.js already tracks contractedBu and pricedBu as two
  // separate, real numbers for exactly this reason (position-fixture.json's
  // HTA/Basis traps) — this file was the one place still collapsing them.
  // contractedUnpricedBu is kept as its own informational number below, never
  // folded back into uncommittedFrac.
  const uncommittedBu = (!contractDataUnknown && expectedBu != null)
    ? Math.max(0, expectedBu - (contractedBu || 0)) : null;
  const uncommittedFrac = (uncommittedBu != null && expectedBu > 0) ? uncommittedBu / expectedBu : null;
  const contractedUnpricedBu = (!contractDataUnknown && contractedBu != null && pricedBu != null)
    ? Math.max(0, contractedBu - pricedBu) : null;

  // RAW (unclamped) days to the board's delivery month — grainBoardHorizon()
  // floors this at 15 for ANY past date, which is correct for a board 15
  // trading days from expiry and wrong for a board that already expired.
  // boardClosed is the distinction; horizonDays (still clamped, still used
  // for the target ladder's rung placement) is unchanged.
  const rawDaysToContract = contractDate
    ? (new Date(String(contractDate).slice(0, 10) + 'T00:00:00') - new Date()) / 86400000 : null;
  const boardClosed = rawDaysToContract != null && rawDaysToContract < 0;
  // resolveBoardHorizon replaces grainBoardHorizon's floor-at-15 and its
  // fail-open 126 for a missing date. boardClosed below is kept because other
  // callers read it, but it now agrees with the module by construction.
  const _bh = resolveBoardHorizon({ contractDate, asOf: (typeof asOfDate === "string" && asOfDate) || todayISO() });
  const horizonDays = _bh.canCarryWorkingOrder ? _bh.tradingDays : null;
  const boardState = _bh.state;
  const ladder = tc ? grainTargetLadder(tc, curve || null, horizonDays || 126) : null;

  // pricedRevenue is not tracked on GrainPosition today (it stores bushels,
  // not a dollar figure) — approximate from avgPricedCashPrice × pricedBu,
  // which is exact when every priced row shares one price and a fair
  // reading otherwise (it is what the weighted average IS for). Computed
  // once here so both `required` below and the before/after scenario reuse
  // the SAME number rather than two independent approximations.
  const pricedRevenue = (pricedBu != null && avgPricedCashPrice != null) ? pricedBu * avgPricedCashPrice : 0;

  // ── Cash vs futures, for economics only (Kevin, 2026-08-17 spec §2) ──
  // cash.available means a real, sourced elevator bid resolved to a delivery
  // contract this crop-year's board also trades. Absent that, fall back to the
  // futures close, but LABEL it as such everywhere it's used (never let a
  // futures number pass as cash — spec §8). Percentile/regime/target-ladder are
  // computed on futures ONLY, above and unchanged.
  //
  // ⚠ AND `available` IS NOT ENOUGH ON ITS OWN (Kevin, 2026-08-28, standing
  // rule): "Do not automatically select one cash bid for the recommendation
  // engine." resolveGrainCash() must still PICK one candidate to be able to
  // answer at all — that part is fine, it is a sourced, dated observation — but
  // its pick is an allowlist rank plus price, not a statement about where this
  // farm sells. Letting that price set breakeven, the before/after scenario and
  // the next cash trigger made an unconfirmed elevator the counterparty of every
  // recommendation on the card, quietly, three different ways.
  //
  // `exampleOnly` is the flag that already means "no confirmed destination for
  // this farm" (grainCash.js resolveLogistics — false ONLY when a farmer-
  // confirmed source list names this bid). It is true for every farm in the book
  // today, so the economics leg runs on FUTURES and says so. Nothing is lost:
  // the observed bids are still returned on `cashOut` for the caller to display
  // NEUTRALLY, they simply no longer drive a recommendation.
  const cashObserved = !!(cash && cash.available && cash.price != null);
  const cashConfirmed = cashObserved && cash.exampleOnly === false;
  const salePrice = cashConfirmed ? cash.price : null;
  const priceForEconomics = salePrice != null ? salePrice : (tc ? tc.last : null);
  const priceLabel = salePrice != null
    ? "the confirmed destination's cash bid"
    : "today's board (futures) price";

  let required = null, belowBreakeven = null;
  if (cop && cop.breakEvenPerBu != null) {
    required = requiredRemainingPrice({ expectedBu, pricedBu, pricedRevenue, breakEvenPerBu: cop.breakEvenPerBu });
    if (priceForEconomics != null) belowBreakeven = priceForEconomics < cop.breakEvenPerBu;
  }

  // BUG-011. The market leg is ranked ACROSS YEARS or not at all — see the
  // note in statusFor(). rankContractAmongYears() already refuses below three
  // comparable contract years, so a thin month lands here as null and the
  // ladder takes its own "insufficient market history" branch.
  const yearPctile = futuresYearRank && futuresYearRank.available
    ? yearRankPercentile(futuresYearRank.rank, futuresYearRank.totalYears) : null;
  const { status, reasons } = statusFor({
    pctile: yearPctile,
    regime: ladder ? ladder.regime : null,
    horizonDays,
    uncommittedFrac,
    contractedUnpricedBu,
    belowBreakeven,
    belowBreakevenPriceLabel: priceLabel,
    boardClosed,
    contractDataUnknown,
    hasExposure,
  });

  // ── marketOpportunityState — the PURE market read, kept structurally
  // separate from `status` (Kevin, 2026-08-18: "separate marketOpportunityState
  // from farm action.status — a strong futures percentile with no position
  // must never manufacture URGENT/sizing"). `status` above is the full,
  // farm-facing 5-way ladder (NO ACTION/WATCH/ATTRACTIVE/STRONG/URGENT) —
  // sizing/urgency ARE gated on hasExposure, but its percentile-only branch
  // still returns a real value even when hasExposure is false, because a
  // Market Mode farm deserves a real market read (§1.2zc). This field is the
  // SAME opportunityCategory() 3-way scale (strong/normal/weak/unknown) the
  // Futures/Basis gauges already use for exactly this signal — reusing that
  // existing vocabulary rather than inventing a fourth one. A caller (e.g. a
  // book-wide ranking) that wants to show or sort Market Mode rows should key
  // off THIS field, never off `status`/STATUS_RANK, so a percentile-only read
  // is never displayed or ranked as if it were a farm-specific action.
  const marketOpportunityState = {
    // "unknown", NOT the life-to-date figure, when the cross-year rank is
    // unavailable. Falling back would restore exactly the defect: a category
    // that reads "strong" for every contract on the board.
    category: yearPctile != null ? opportunityCategory(yearPctile) : "unknown",
    pctile: yearPctile,
    basis: yearPctile != null ? "cross_year_rank" : "unavailable",
    reason: yearPctile != null ? null
      : (futuresYearRank && futuresYearRank.reason) || "no cross-year contract-month archive at this point in contract life",
    // The board's own life-to-date percentile, kept for the detail layer and
    // NAMED so nobody reads it as comparable across contracts.
    lifePctile: tc ? tc.pctile : null,
    hasExposure,
  };

  // Suggested amount: the plan's nearest attainable rung's bushel share of
  // what has NO CONTRACT AT ALL yet — layered, never all-or-nothing
  // (ROADMAP.md §1.2). Sized off uncommittedBu, not the old unpriced figure:
  // a bushel already under an HTA/basis-open contract has a marketing
  // decision on it, and suggesting a NEW sale against it would double-count
  // that bushel. Reuses the SAME ladder the Model Farm / Market Intelligence
  // tools show; this file adds no second sizing rule. Withheld entirely when
  // the contract position is unknown — sizing a sale off an assumed-zero-
  // sold position could recommend re-selling bushels the farm already has
  // under contract, exactly the harm ROADMAP.md's "never present an action
  // we cannot defend" rule exists to stop.
  let suggestedBu = null, suggestedBuExact = null, nextTrigger = null, nextTriggerCash = null, nextTriggerBasisAssumption = null;
  // targetPosition — POSITION -> TARGET RANGE -> GAP (Kevin, 2026-08-19
  // overnight watchdog item 1, src/positions/grainTargetPosition.js). Always
  // computed (not just inside the `ladder.levels.length` branch below) so a
  // caller can render "target range: 85% -> 100%" even on a WATCH/NO ACTION
  // read where widenPct is 0 and gapBu is 0 — that IS the honest answer
  // ("already at the target for today's market"), not an absence of one.
  // Gated exactly like the prior formula was: only when contract
  // completeness is KNOWN (contractDataUnknown false) and a real exposure
  // denominator exists — unknown/partial position still returns
  // known:false, never a false-precision bushel figure (Kevin: "If current
  // position is partial/unknown, recommend a target %/range only — no
  // false-precision bushels" — and here even the %/range is withheld,
  // because grainCommitment.js's own pctPriceProtected is ALSO null in that
  // case, since it derives from the same zero-rows-on-file signal
  // contractDataUnknown does).
  const targetPosition = (hasExposure && !contractDataUnknown)
    // crop/cropYear come straight off the GrainPosition this function was
    // already handed (position.commodity/position.cropYear, set by
    // grainPosition.js's makePosition()) — no new plumbing. `asOf` (2026-08-19
    // correction: grainMarketingStagePolicy.js now carries a REAL sourced
    // CFFM/Usset stage policy, keyed off crop+cropYear+asOf) is an optional
    // caller-supplied 'YYYY-MM-DD' — this module stays PURE (no Date.now()/
    // new Date() of its own); the caller (App.jsx's computeGrainActionResult,
    // which does have a real clock) supplies "today". Omitting it (every
    // existing caller that predates this correction) falls back to the
    // original current-position-relative +15/+30 band, unchanged.
    ? resolveGrainTargetGapBu({ status, pctPriceProtected, priceProtectedBu, expectedBu, crop: position?.commodity ?? null, cropYear: position?.cropYear ?? null, asOf })
    : { known: false, range: { known: false, reason: hasExposure ? "contract completeness unknown for this crop/year" : "no farm-specific exposure resolved" }, gapBuExact: null, gapBu: null, gapPct: null, targetBu: null };
  // nextTrigger/nextTriggerCash are pure MARKET/BASIS values (a futures rung,
  // optionally converted to an implied cash target) — they need no farm
  // exposure denominator, so they are no longer gated on expectedBu (2026-
  // 08-18 Market Mode fix). Bushel SIZING below is still strictly gated on a
  // credible exposure + known contract position; the two were previously
  // gated together, which hid a real market target on every no-exposure farm.
  if (ladder && ladder.levels.length) {
    const rung = ladder.levels[0];   // nearest ("attainable") rung
    nextTrigger = rung.lvl;
    // Kevin, 2026-08-17 spec §6: the ladder's own rungs are futures levels
    // (grainTargetLadder never touches basis). Convert the near rung to an
    // implied CASH target using the SAME basis the economics leg used — an
    // assumption, so it is shown alongside its source and never in place of the
    // futures number.
    //
    // ⚠ GATED ON A CONFIRMED DESTINATION 2026-08-28, not merely on a resolved
    // bid. A cash trigger is the most instruction-shaped number on the card —
    // "next cash target $5.50 at <elevator>" reads as where to sell — and it was
    // being built from whichever bid the allowlist rank happened to pick. With
    // no confirmed destination the FUTURES trigger stands alone, which is the
    // number that is true regardless of counterparty.
    if (cashConfirmed && cash.basis != null) {
      nextTriggerCash = rung.lvl + cash.basis;
      nextTriggerBasisAssumption = { basis: cash.basis, source: cash.source, sourceLabel: cash.sourceLabel, deliveryPeriod: cash.deliveryPeriod, assumedConstant: true };
    }
    // suggestedBuExact is now the TARGET-RANGE GAP (grainTargetPosition.js),
    // never the flat `uncommittedBu * 0.30/0.15` fraction — that formula
    // reapplied a fixed % of an ever-shrinking remainder and never converged.
    // Still gated on `ladder.levels.length` (a real board rung must exist to
    // act against) plus targetPosition.known (a real starting position must
    // exist to measure a gap from) — the same two preconditions the prior
    // formula required (a ladder AND a known contract position).
    if (targetPosition.known && targetPosition.gapBuExact > 0) {
      suggestedBuExact = targetPosition.gapBuExact;
    }
  }
  // ── Practical lot sizing (Kevin, 2026-08-17): Silas only ever recommends
  // 1,000-bu increments, rounded DOWN so it never suggests committing more
  // than the deterministic calculation intended. suggestedBuExact keeps the
  // precise number for anyone who wants it; every downstream use (scenario,
  // yield-risk, the card) reads suggestedBu — the rounded, executable figure.
  if (suggestedBuExact != null) suggestedBu = roundDownToLot(Math.round(suggestedBuExact));
  // ── ONE STEP, NOT THE WHOLE GAP (see SALE_STEP_PCT) ────────────────────
  // suggestedBuExact stays the deterministic gap and suggestedBuUncapped the
  // lot-floored version of it; only the RECOMMENDED figure is capped, so how
  // the number was reached remains auditable at every stage.
  const expectedForStep = position?.exposure?.expectedBu;
  const saleStepCapBu = (expectedForStep > 0) ? roundDownToLot(Math.round(expectedForStep * SALE_STEP_PCT)) : null;
  const suggestedBuUncapped = suggestedBu;
  const saleStepCapped = saleStepCapBu != null && saleStepCapBu > 0 && suggestedBu != null && suggestedBu > saleStepCapBu;
  if (saleStepCapped) suggestedBu = saleStepCapBu;
  // Kevin, 2026-08-17 (this session's spec §2 / "NEXT WORK" reconciliation):
  // "For a raw suggestion below 1,000 bu, do not round UP and overcommit.
  // Return an explicit sub-lot/no-action reason rather than fabricating a
  // 1,000-bu recommendation." roundDownToLot already floors a sub-lot amount
  // to a real 0 (never invents a lot) — this just makes that WHY visible
  // instead of a silent 0 indistinguishable from "no signal at all".
  const subLot = suggestedBuExact != null && suggestedBuExact > 0 && suggestedBu === 0;
  if (subLot) {
    reasons.push(`the deterministic sizing found ${Math.round(suggestedBuExact).toLocaleString()} bu, below one practical 1,000-bu lot — no actionable sale sized rather than rounding up and overcommitting`);
  }

  // ── Before/after scenario (Kevin, 2026-08-17 spec §7, revised for cash on
  // 2026-08-17 spec §2/§7): what does taking the suggested action actually
  // change, at the price the farm could ACTUALLY book. Reuses
  // requiredRemainingPrice() for the "after" leg rather than a second
  // breakeven calculation. Recomputed from `suggestedBu` — the LOT-ROUNDED,
  // practical quantity, never the pre-rounded suggestedBuExact — so before/
  // after always reflects the sale a farmer could actually place (Kevin,
  // 2026-08-17 spec §1). assumedPrice prefers the resolved cash bid; falls
  // back to the futures close ONLY when no cash source is available, and
  // `priceIsCash` says which one so the caller never mislabels a futures
  // number as an executable cash price (spec §8). Withheld (null) whenever
  // there is nothing to suggest — no fabricated scenario for a WATCH/NO
  // ACTION read.
  let scenario = null;
  if (suggestedBu > 0 && tc && expectedBu != null && expectedBu > 0) {
    const assumedPrice = salePrice != null ? salePrice : tc.last;
    const beforePricedBu = pricedBu || 0;
    const afterPricedBu = beforePricedBu + suggestedBu;
    const afterRevenue = pricedRevenue + suggestedBu * assumedPrice;
    const afterAvgPrice = afterPricedBu > 0 ? afterRevenue / afterPricedBu : null;
    let afterRequired = null;
    if (cop && cop.breakEvenPerBu != null) {
      afterRequired = requiredRemainingPrice({ expectedBu, pricedBu: afterPricedBu, pricedRevenue: afterRevenue, breakEvenPerBu: cop.breakEvenPerBu });
    }
    scenario = {
      assumedPrice,
      priceIsCash: salePrice != null,
      saleBu: suggestedBu,
      before: {
        pricedBu: beforePricedBu,
        pctPriced: +(beforePricedBu / expectedBu * 100).toFixed(1),
        openBu: Math.max(0, expectedBu - beforePricedBu),
        avgPrice: avgPricedCashPrice,
        requiredRemainingPrice: required ? required.requiredPrice : null,
      },
      after: {
        pricedBu: afterPricedBu,
        pctPriced: +(afterPricedBu / expectedBu * 100).toFixed(1),
        openBu: Math.max(0, expectedBu - afterPricedBu),
        avgPrice: afterAvgPrice,
        requiredRemainingPrice: afterRequired ? afterRequired.requiredPrice : null,
      },
    };
  }

  // ── Yield-risk scenarios (Kevin, 2026-08-17 spec §8): "do not recommend
  // aggressive forward sales using only expected production." Two tiers,
  // both SOURCED — never a fabricated third. guaranteeBu is RMA's own
  // insured floor (position.exposure.guaranteeBu — the SAME number
  // overSoldNote() in App.jsx already compares total commitment against,
  // reused here rather than re-derived). expectedBu is this file's own
  // already-computed expected production. There is no sourced UPSIDE yield
  // anywhere in this codebase today (no per-farm historical-max APH is
  // wired into GrainPosition) — inventing one (a symmetric band, a trend
  // bump) would be exactly the "material unsourced economic assumption"
  // AGENTS.md requires stopping for. Recorded as a real gap in ROADMAP.md,
  // not silently patched over.
  //
  // committedAfterBu is TOTAL CONTRACTED bushels (delivery obligation, not
  // just cash-priced) plus the suggested sale — overselling risk is about
  // what you're on the hook to DELIVER, not just what has a cash price.
  // NOT clamped at 100% (CLAUDE.md's own rule): a position already over a
  // tier's production is exactly the exposure this exists to surface.
  let yieldScenarios = null;
  // hasExposure guards `position.exposure.guaranteeBu` below — with the
  // 2026-08-18 Market Mode fix, contractDataUnknown alone no longer implies
  // position.exposure is non-null (a farm can have known contracts but no
  // RMA exposure — grainPosition.js's own "partial" state), so this must be
  // checked explicitly rather than assumed from the early-return this file
  // used to have.
  if (hasExposure && !contractDataUnknown && position.exposure.guaranteeBu != null && expectedBu != null) {
    const committedBeforeBu = contractedBu || 0;
    const committedAfterBu = committedBeforeBu + (suggestedBu || 0);
    const tier = (label, productionBu) => {
      if (!(productionBu > 0)) return { label, productionBu: null, committedAfterBu, pctCommitted: null, openBu: null, oversoldBu: null };
      const pctCommitted = +(committedAfterBu / productionBu * 100).toFixed(1);
      return {
        label, productionBu, committedAfterBu, pctCommitted,
        openBu: Math.max(0, productionBu - committedAfterBu),
        oversoldBu: Math.max(0, committedAfterBu - productionBu),
      };
    };
    yieldScenarios = [
      tier("Downside (RMA insured guarantee)", position.exposure.guaranteeBu),
      tier("Expected", expectedBu),
    ];
  }

  // ── cash, for the caller to render/audit (Kevin, 2026-08-17 spec §7/§8) ──
  // Always present, never omitted: an unavailable cash source is itself a
  // fact the card must show ("cash price unavailable"), not silence.
  const cashOut = cashObserved
    ? { available: true, price: cash.price, futures: cash.futures, basis: cash.basis,
        source: cash.source, sourceLabel: cash.sourceLabel,
        // Bid structure (docs/grain-gauge-build.md) — DELIVERED/PICKUP/
        // DIRECT_SHIP/UNKNOWN, passed straight through from resolveGrainCash;
        // never re-derived here.
        bidStructure: cash.bidStructure || "unknown",
        deliveryPeriod: cash.deliveryPeriod,
        deliveryContract: cash.deliveryContract, asOf: cash.asOf,
        periodAlternatives: cash.periodAlternatives || [], candidates: cash.candidates || [],
        // Geography (Kevin, 2026-08-17 reconciliation §A) and basis history
        // context (§B) — passed straight through from resolveGrainCash so the
        // card can show WHY this source was selected, never re-derived here.
        geographyConfidence: cash.geographyConfidence || "unknown",
        distanceMi: cash.distanceMi ?? null, geographyEligible: cash.geographyEligible ?? null,
        geographyNote: cash.geographyNote || null, basisContext: cash.basisContext || { available: false },
        // logisticsConfidence/exampleOnly/priceLabel/otherPricedCandidates
        // (Kevin, 2026-08-19 mobile review — "Action still says HOWLETT
        // FARMS — GENEVA for a farm that will never deliver to Geneva"):
        // passed straight through from resolveGrainCash, never re-derived —
        // this is the flag a render layer MUST check before naming a
        // destination as the farm's execution venue.
        logisticsConfidence: cash.logisticsConfidence || "unknown",
        exampleOnly: cash.exampleOnly !== false,
        priceLabel: cash.priceLabel || null,
        otherPricedCandidates: cash.otherPricedCandidates || [],
        // usedForEconomics says PLAINLY whether this observation drove any
        // recommendation on the card. FALSE for every farm in the book today
        // (no confirmed destination exists anywhere), so a render layer cannot
        // mistake an observed bid for the number breakeven, the before/after
        // scenario and the next trigger were actually computed on.
        usedForEconomics: cashConfirmed }
    : { available: false, reason: cash && cash.reason ? cash.reason : "no cash/basis source resolved for this crop/delivery period", candidates: (cash && cash.candidates) || [] };

  // storage.exposure — the ONE mechanical derivation Kevin asked for
  // 2026-08-18 21:45 ("storage capacity must become position-relevant,
  // without inventing deadlines"): expectedCropBu vs. storageCapacityBu ->
  // harvestDeliveryRequiredBu / storableBu. Pure arithmetic over fields this
  // function already has (expectedBu, storage.mode/capacityBu) — it reads
  // NOTHING computed above (status/marketOpportunityState/suggestedBu/
  // nextTrigger) and nothing above reads it back; a caller can drop it
  // without touching any other field. Still a passthrough in spirit: the
  // storage MODE/CAPACITY themselves are untouched input, only the split is
  // new.
  const storageIn = storage || { known: false, mode: null, capacityBu: null, updatedAt: null };
  const storageOut = {
    ...storageIn,
    exposure: deriveStorageExposure({ mode: storageIn.mode, capacityBu: storageIn.capacityBu, expectedCropBu: expectedBu }),
  };
  // harvestExecution — HARVEST OUTLET REQUIRED vs. STORAGE OPTIONALITY
  // (Kevin, 2026-08-19 final correction: "storage must affect harvest
  // exposure/action, not only display"). A SEPARATE, explicit output from
  // grainHarvestExecution.js — never status/marketOpportunityState/
  // suggestedBu/targetPosition (all computed above already and untouched
  // here; check-grain-storage.js's own "storage does NOT change status/
  // suggestedBu/marketOpportunityState" invariance stays true). It reads
  // storage.exposure (just computed) + commitment.physicalBu (the delivery-
  // obligation figure, NOT priceProtectedBu — Kevin was explicit that a
  // price-protected HTA may also be physically committed, and outlet
  // coverage is a logistics fact, not a pricing one).
  const harvestExecution = resolveHarvestExecution({
    storageExposure: storageOut.exposure,
    physicalBu: commitment?.physicalBu ?? null,
    expectedBu,
  });
  // Reasons text (Kevin, 2026-08-18 mobile review) stays INFORMATIONAL only —
  // it is derived from harvestExecution above, never the other direction, so
  // it can never diverge from the structured field a caller renders.
  if (harvestExecution.known && harvestExecution.harvestOutletGapBu > 0) {
    const coveredNote = harvestExecution.harvestOutletCoveredBu > 0
      ? ` (${Math.round(harvestExecution.harvestOutletCoveredBu).toLocaleString()} bu of that already has a delivery outlet under contract)`
      : "";
    reasons.push(`${Math.round(harvestExecution.harvestOutletGapBu).toLocaleString()} bu still need a harvest outlet arranged — storage capacity is full/unavailable for them${coveredNote}, independent of price`);
  } else if (harvestExecution.known && harvestExecution.harvestOutletRequiredBu > 0) {
    reasons.push(`all ${Math.round(harvestExecution.harvestOutletRequiredBu).toLocaleString()} bu requiring a harvest outlet are already covered by an existing delivery commitment`);
  }

  // saleReason — composes marketOpportunityState + the resolved cash
  // candidate's own basisContext into OPPORTUNITY/RELATIVE-OPPORTUNITY/
  // DISCIPLINE (2026-08-18 item 5). DISCIPLINE never fires — see
  // grainSaleReason.js header — this is a classification of ALREADY-
  // COMPUTED signals, not a new economics input.
  const saleReason = classifySaleReason({
    marketOpportunityState,
    basisContext: cashOut && cashOut.available ? cashOut.basisContext : null,
    logisticsConfirmed: cashOut && cashOut.available && cashOut.logisticsConfidence === "confirmed",
  });

  return {
    status, reasons,
    marketOpportunityState,
    // storage: unread by everything above — a pure passthrough plus the
    // mechanical exposure split (see the header note on the `storage`
    // parameter). null/undefined in -> the caller's own "not known" shape
    // out, never defaulted to a mode here.
    storage: storageOut,
    // harvestExecution — HARVEST OUTLET REQUIRED vs. STORAGE OPTIONALITY
    // (grainHarvestExecution.js). A logistics/exposure claim, NOT a market
    // or destination claim: known:false whenever storage.exposure or
    // physical commitment is unresolved, never a fabricated bushel figure.
    harvestExecution,
    // regime: pure passthrough (see the `regime` parameter doc above) —
    // null when the caller hasn't computed grainRegime.js's reading.
    regime,
    // saleReason: OPPORTUNITY/RELATIVE-OPPORTUNITY/DISCIPLINE classification
    // over already-computed signals (see grainSaleReason.js).
    saleReason,
    // targetPosition — POSITION -> TARGET RANGE -> GAP (grainTargetPosition.js,
    // Kevin 2026-08-19 item 1). `known:false` (with `range.reason`) whenever
    // contract completeness or exposure is not credible — the caller must
    // show a %/range claim only when `known` is true, never bushels either
    // way when `known` is false.
    targetPosition,
    // commitment — PHYSICAL vs PRICE-PROTECTION split (grainCommitment.js via
    // grainPosition.js), passed straight through for the caller to render
    // (item 2: "do not display contracted/priced as interchangeable"). null
    // when position/currentProtection itself is unavailable.
    commitment: commitment || null,
    suggestedBu, suggestedBuExact, subLot, nextTrigger, nextTriggerCash, nextTriggerBasisAssumption,
    // The step cap and what the figure would have been without it, so the
    // card can show "14,000 now" beside "52,000 to target" and a checker can
    // test the lot rounding and the cap as separate rules.
    suggestedBuUncapped, saleStepCapBu, saleStepCapped,
    cop: cop ? { breakEvenPerBu: cop.breakEvenPerBu, costPerAcre: cop.costPerAcre, source: cop.mode } : null,
    market: tc ? { last: tc.last, pctile: yearPctile, lifePctile: tc.pctile, pctileBasis: yearPctile != null ? "cross_year_rank" : "unavailable", regime: ladder ? ladder.regime : null, horizonDays,
                   boardClosed, rungs: ladder ? ladder.levels : [] } : null,
    requiredRemainingPrice: required ? required.requiredPrice : null,
    // Provenance flags (SILAS_ACTION_INTELLIGENCE.md data-quality ask,
    // 2026-08-17) — surfaced on the object itself, not buried in reasons[]
    // text, so a caller can gate confidence/UI treatment on them directly.
    // hasExposure (2026-08-18) is the one flag that distinguishes true
    // MARKET MODE (no farm-specific denominator at all — market/cash/basis
    // are still real, sizing/scenario/yield-risk are withheld) from every
    // other data-quality state, which all still have an expectedBu.
    hasExposure,
    contractDataUnknown,
    boardClosed,
    uncommittedBu,
    contractedUnpricedBu,
    scenario,
    yieldScenarios,
    cash: cashOut,
  };
}
